/** Step-by-step request capture (built for phones). */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CustomerResponse, RequestResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { MutationError } from "../shared.js";
import { CustomerPicker, Notice, useCustomer } from "../commercial/document.js";
import { Step } from "./request-common.js";
import {
  type PendingPhoto,
  PhotoPicker,
  QuickCustomerDialog,
  type RequestDraft,
  RequestFields,
  draftValid,
  emptyDraft,
  requestPayload,
  uploadPhotos,
} from "./RequestForm.js";

export function NewRequestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const [params] = useSearchParams();
  const preselected = useCustomer(params.get("customerId"));
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [draft, setDraft] = useState<RequestDraft>(emptyDraft);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [saved, setSaved] = useState<RequestResponse | null>(null);
  const [uploadFailures, setUploadFailures] = useState(0);
  useEffect(() => {
    if (preselected.data && !customer) setCustomer(preselected.data);
  }, [preselected.data, customer]);

  const save = useMutation({
    mutationFn: async (submit: boolean) => {
      const response = await apiPost<{ data: RequestResponse }>("/requests", {
        ...requestPayload(
          draft,
          customer!,
          has("finance.view"),
          has("requests.assign"),
        ),
        submit,
      });
      if (photos.length) {
        const failed = await uploadPhotos(
          response.data.id,
          photos,
          (key, progress, error) =>
            setPhotos((current) =>
              current.map((photo) =>
                photo.key === key ? { ...photo, progress, error } : photo,
              ),
            ),
        );
        setUploadFailures(failed);
      }
      return response.data;
    },
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: ["requests"] });
      setSaved(request);
      window.scrollTo({ top: 0 });
    },
  });

  if (saved) {
    return (
      <div className="mx-auto max-w-md py-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-success">
          <i className="pi pi-check text-2xl" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-semibold">
          {saved.status === "draft"
            ? t("request.savedDraft")
            : t("request.submitted")}
        </h1>
        <p className="mt-2 text-app-muted">
          <span className="font-mono">{saved.number}</span> ·{" "}
          {saved.customer.displayName}
        </p>
        <div className="mt-2">
          <StatusBadge status={saved.status} />
        </div>
        {uploadFailures ? (
          <div className="mt-4">
            <Notice tone="warning">
              {t("request.uploadFailures", { count: uploadFailures })}
            </Notice>
          </div>
        ) : null}
        <div className="mt-6 flex flex-col gap-2">
          <Button
            icon="pi pi-plus"
            onClick={() => {
              setSaved(null);
              setDraft(emptyDraft());
              setPhotos([]);
              setCustomer(null);
              setUploadFailures(0);
            }}
          >
            {t("request.captureAnother")}
          </Button>
          <Button
            variant="outlined"
            severity="secondary"
            onClick={() => navigate(`/requests/${saved.id}`)}
          >
            {t("request.viewRequest")}
          </Button>
        </div>
      </div>
    );
  }

  const valid = Boolean(customer) && draftValid(draft, has("finance.view"));
  return (
    <form
      className="mx-auto max-w-2xl space-y-4 pb-40 md:pb-24"
      onSubmit={(event) => event.preventDefault()}
    >
      <PageHeader
        title={t("request.new")}
        subtitle={t("request.newSubtitle")}
      />
      <Step number={1} title={t("request.stepCustomer")}>
        <CustomerPicker
          id="req-customer"
          value={customer}
          onChange={(next) => {
            setCustomer(next);
            setDraft((current) => ({ ...current, siteChoice: "billing" }));
          }}
        />
        {!customer && has("customers.create") ? (
          <Button
            type="button"
            variant="text"
            icon="pi pi-user-plus"
            onClick={() => setQuickOpen(true)}
          >
            {t("request.newCustomer")}
          </Button>
        ) : null}
      </Step>
      {customer ? (
        <>
          <RequestFields
            draft={draft}
            onChange={setDraft}
            customer={customer}
            firstStep={2}
          />
          {has("files.upload") ? (
            <Step number={5} title={t("request.stepPhotos")}>
              <PhotoPicker photos={photos} onChange={setPhotos} />
            </Step>
          ) : null}
        </>
      ) : null}
      <MutationError error={save.error} />
      <div className="fixed inset-x-0 bottom-16 z-20 border-t border-app-border bg-app-surface/95 px-4 py-3 backdrop-blur md:sticky md:bottom-0 md:rounded-xl md:border">
        <div className="mx-auto flex max-w-2xl gap-2">
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            className="flex-1 md:flex-none"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate(false)}
          >
            {t("request.saveDraft")}
          </Button>
          <Button
            type="button"
            className="flex-1 md:ml-auto md:flex-none"
            icon="pi pi-send"
            loading={save.isPending || undefined}
            disabled={!valid}
            onClick={() => save.mutate(true)}
          >
            {t("request.submitToOffice")}
          </Button>
        </div>
      </div>
      <QuickCustomerDialog
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreated={(created) => {
          setCustomer(created);
          setQuickOpen(false);
        }}
      />
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Detail                                                              */
/* ------------------------------------------------------------------ */
